/**
 * Structured error handling for the mini-app `apiFetch` boundary.
 *
 * `FetchErrorLike` is the canonical shape, declared in the dependency-free
 * leaf `mini-apps/_shared/fetch-error-types.ts` (alongside the base
 * `FetchError` that `web/app-home/src/lib/errors.ts` imports from the same
 * leaf) — see that file's doc-comment for the full rationale. In short: this
 * module is imported by `mini-apps/_shared/__tests__/fetch-error.test.ts`,
 * which is type-checked by `tsc -p tsconfig.tests.json` (backend/commonjs
 * config), and `errors.ts` transitively imports
 * `web/app-home/src/lib/logger.ts`, which uses `import.meta` — valid under
 * the frontend/Vite (`esnext`-module) config but a hard `tsc` error under
 * commonjs. That's why the shared shape lives in a zero-import leaf instead
 * of in `errors.ts` directly.
 */
import { z } from 'zod';

import type { FetchErrorLike } from './fetch-error-types';
import { WriteRefusedError } from '../../web/app-home/src/lib/apiProblemError';
import { extractUserMessage } from '../../src/shared/error-message';

export type { FetchErrorLike };

/**
 * Core `apiFetch`'s policy refusal (read-only impersonation 403, external-write
 * sandbox 423). Core toasts it once, then rejects with this before
 * `wrapThrowingApiFetch` sees a Response, so mini-app code receives it in
 * place of a `MiniAppFetchError` and must not toast it again.
 */
export { WriteRefusedError };

/**
 * Plan 2026-09-28-008 U4 (R20) — the one generic, digit-free fallback string
 * a rep can be shown when there is nothing more specific to say. Moved above
 * `MiniAppFetchError` (it used to live only near `displayMessageForFetchError`,
 * below) because the constructor's own message fallback needs it too.
 */
const GENERIC_FETCH_ERROR_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Real `Error` subclass carrying the `FetchErrorLike` fields.
 *
 * `throwFetchError` used to throw a plain object literal typed as
 * `FetchErrorLike` — structurally correct for the `isFetchError`/
 * `classifyError` duck-typing contract (`typeof err.code === 'string'`), but
 * NOT an `instanceof Error`. Several mini-app catch sites do
 * `err instanceof Error ? err.message : '<generic>'`
 * (`mini-apps/daily-digest/index.tsx`, `mini-apps/egs-onboarding/index.tsx`)
 * — against a plain object those always fell through to the generic
 * fallback, silently discarding error fidelity. Throwing a real `Error`
 * subclass fixes those sites for free without touching them: `code` stays a
 * plain instance property, so every existing `isFetchError`/`classifyError`
 * check (which only inspects `.code`) keeps working unchanged.
 */
export class MiniAppFetchError extends Error implements FetchErrorLike {
  code: string;
  oauthUrl?: string;
  userMessage?: string;
  status: number;
  retryAfterSec?: number;

  constructor(fields: FetchErrorLike) {
    // Plan 2026-09-28-008 U4 (R20) — a rep must never see `http_502:502`
    // (this used to be the message fallback for a non-JSON error body, e.g.
    // an HTML error page from a proxy/CDN); `code`/`status` are unchanged,
    // since every consumer reads those, not `message`.
    super(fields.message ?? GENERIC_FETCH_ERROR_FALLBACK);
    this.name = 'MiniAppFetchError';
    this.code = fields.code;
    this.oauthUrl = fields.oauthUrl;
    this.userMessage = fields.userMessage;
    this.status = fields.status;
    this.retryAfterSec = fields.retryAfterSec;
  }
}

type RawApiFetch = (
  input: RequestInfo | URL,
  init?: RequestInit & { timeoutMs?: number },
) => Promise<Response>;

const optionalString = z.string().optional().catch(undefined);

/**
 * A failed response's error envelope, read field by field: a field of the
 * wrong type is dropped, never fatal, and a body that is not an object (or not
 * JSON) reads as empty.
 */
const ErrorBodySchema = z
  .looseObject({
    code: optionalString,
    oauthUrl: optionalString,
    userMessage: optionalString,
    detail: optionalString,
    title: optionalString,
    message: optionalString,
    retryAfterSec: z.number().optional().catch(undefined),
    error: z
      .union([z.string(), z.looseObject({ code: optionalString, message: optionalString })])
      .optional()
      .catch(undefined),
  })
  .catch({});

/**
 * Parse a failed Response's JSON error body into a `FetchErrorLike` and
 * throw it.
 *
 * `code` fallback chain: `body.code` → `body.error.code` (core Terra's
 * nested `{ error: { code, message } }` envelope) → `body.error`
 * (string-typed machine code) → `http_<status>` — the last used when the
 * body is missing, not JSON (e.g. an HTML error page from a proxy/CDN), or
 * no field matches.
 *
 * `userMessage`/message fallback chain: `body.userMessage` → `body.detail`
 * → `body.title` → `body.message` → `body.error.message` (string-typed
 * human-readable text only). A string-typed `body.error` is deliberately
 * excluded from this chain — backends use it for machine codes (e.g.
 * `{ error: 'not_found' }`), not user-facing copy, so it must only ever
 * feed `code`, never `userMessage`. The nested envelope's `error.message`
 * IS user-facing copy (see e.g. `src/api/flow-enroll.ts`), so it closes
 * the chain.
 *
 * `retryAfterSec`: a 429's own wait — `body.retryAfterSec` (problem+json
 * bodies that carry it) or the `Retry-After` header in seconds — so a client
 * can honour the server's countdown instead of guessing one.
 */
export async function throwFetchError(res: Response): Promise<never> {
  const fallbackCode = `http_${res.status}`;
  let rawBody: unknown = null;
  try {
    rawBody = await res.json();
  } catch (err) {
    // A cancelled body read is not "no body": callers classify it as aborted.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
  }
  const body = ErrorBodySchema.parse(rawBody);
  const nestedError = body.error && typeof body.error === 'object' ? body.error : null;

  const code =
    body.code ??
    nestedError?.code ??
    (typeof body.error === 'string' ? body.error : undefined) ??
    fallbackCode;
  const oauthUrl = body.oauthUrl;
  const userMessage = extractUserMessage(rawBody);

  const retryAfterHeader = Number(res.headers.get('Retry-After'));
  const retryAfterSec =
    typeof body.retryAfterSec === 'number' && body.retryAfterSec > 0 ? body.retryAfterSec :
    Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader :
    undefined;

  throw new MiniAppFetchError({
    code,
    oauthUrl,
    userMessage,
    message: userMessage,
    status: res.status,
    retryAfterSec,
  });
}

/**
 * Compose a throwing `apiFetch` from a raw (Response-returning) one.
 *
 * This is the deliberate asymmetry vs core Terra's `apiFetch`: core
 * resolves with the raw Response for every status except a policy refusal,
 * which it rejects as a `WriteRefusedError` (`web/app-home/src/lib/api.ts`); the
 * mini-app boundary throws a structured `FetchErrorLike` on any HTTP
 * failure so "forgot to translate the error" is structurally impossible for
 * mini-app call sites. See `mini-apps/_shared/hooks/useApi.ts` for the
 * exported `apiFetch` (throwing) / `apiFetchRaw` (passthrough, this
 * function's input) pair.
 *
 * - On success (`res.ok`), returns the Response untouched — callers keep
 *   their own `.json()` / stream reads.
 * - On failure (`!res.ok`), parses the body and throws via
 *   `throwFetchError` instead of resolving.
 * - Rejections of the raw fetch (network failures, and core's
 *   `WriteRefusedError`) propagate unchanged — they are never wrapped into
 *   a `MiniAppFetchError`.
 */
export function wrapThrowingApiFetch(rawApiFetch: RawApiFetch): RawApiFetch {
  return async (input, init) => {
    const res = await rawApiFetch(input, init);
    if (!res.ok) {
      await throwFetchError(res);
    }
    return res;
  };
}

/**
 * A safe, user-facing display string for a thrown fetch error — never the
 * raw machine `message`.
 *
 * `MiniAppFetchError#message` is pinned wire-contract text (see
 * `throwFetchError`'s doc comment and this file's tests) and, for a
 * non-JSON error body (an HTML 502 page from a proxy, a 4xx with no
 * recognizable text field), that message is now `GENERIC_FETCH_ERROR_FALLBACK`
 * (plan 2026-09-28-008 U4, R20 — it used to be `http_<status>:<status>`,
 * e.g. `http_502:502`, which is exactly the kind of raw code R20 forbids).
 * Several Action View render sites used to show `err.message` verbatim in
 * that case, surfacing a raw code to a rep (`SettingsPanel`'s "Save failed:
 * …" banner, `NotificationRuleDrawer`'s `deriveNotificationMutationErrorMessage`,
 * `ActionQueuePage`'s load-error state). This helper is the one place that
 * decides what's safe to show: the server's own `userMessage` when one was
 * parsed, otherwise a caller-supplied (or built-in) digit-free generic —
 * never `message` itself.
 *
 * The non-`MiniAppFetchError` branch used to fall back to `err.message`
 * when the thrown value was a real `Error` — which meant an underlying
 * `fetch()` network rejection (`TypeError: Failed to fetch`) or a
 * client-side abort (`DOMException: signal is aborted without reason`,
 * the exact shape `web/app-home/src/lib/api.ts`'s `controller.abort()`
 * produces) rendered its raw, un-user-facing text verbatim in the same
 * banners. Neither is a `MiniAppFetchError` (they never reach
 * `throwFetchError`'s body-parsing), so there is no `userMessage` to prefer
 * — and no other message on a non-`MiniAppFetchError` is safe to show
 * either. Any other non-`MiniAppFetchError` gets the fallback; the one
 * exception is a `WriteRefusedError`, whose `userMessage` is always set.
 */
export function displayMessageForFetchError(err: unknown, fallback: string = GENERIC_FETCH_ERROR_FALLBACK): string {
  if (err instanceof WriteRefusedError) return err.userMessage;
  return err instanceof MiniAppFetchError ? (err.userMessage ?? fallback) : fallback;
}
